import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Building2, Plus, Loader2 } from 'lucide-react';
import { useDeal } from '../contexts/DealContext';
import { formatCurrency } from '../types/database';
import type { UserDeal } from '../types/database';

export function DealSelector() {
  const { selectedDeal, availableDeals, isLoading, selectDeal } = useDeal();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Check if user has signed NDA for a deal - now using deal's NDA data directly
  const hasSignedNda = (deal: UserDeal) => {
    return deal.nda_signed === true;
  };

  const handleDealSelect = (deal: any) => {
    selectDeal(deal);
    navigate(`/deals/${deal.deal_id}/dashboard`);
    setIsOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
        <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
        <span className="text-sm text-gray-600">Loading deals...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Deal Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[200px]"
      >
        <Building2 className="w-4 h-4 text-purple-600" />
        <div className="flex-1 text-left">
          {selectedDeal ? (
            <div>
              <div className="text-sm font-medium text-gray-900 truncate">
                {(() => {
                  // Check if user has signed NDA for this deal
                  const hasNda = hasSignedNda(selectedDeal);
                  
                  // For read-only users, show business_name if NDA is signed, otherwise business_alias
                  if (selectedDeal.access_type === 'read') {
                    if (hasNda) {
                      return selectedDeal.business_name || 'Business Opportunity';
                    } else {
                      // Use business_alias (guaranteed to exist due to database constraints)
                      return selectedDeal.business_alias;
                    }
                  }
                  // For admin/write users, always show business_name
                  return selectedDeal.business_name || 'Business Opportunity';
                })()}
              </div>
              {selectedDeal.deal_value && (
                <div className="text-xs text-gray-500">
                  {formatCurrency(selectedDeal.deal_value, selectedDeal.currency)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-500">Select a deal</div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {/* Create New Deal Option */}
          <Link
            to="/deals/create"
            className="flex items-center gap-2 px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 border-b border-gray-100"
            onClick={() => setIsOpen(false)}
          >
            <Plus className="w-4 h-4" />
            Create New Deal
          </Link>

          {/* Available Deals */}
          {availableDeals.length > 0 ? (
            <div className="py-1">
              {availableDeals.map((deal) => (
                <button
                  key={deal.deal_id}
                  onClick={() => handleDealSelect(deal)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    selectedDeal?.deal_id === deal.deal_id ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                  }`}
                >
                  <div className="font-medium truncate">
                    {(() => {
                      // Check if user has signed NDA for this deal
                      const hasNda = hasSignedNda(deal);
                      
                      // For read-only users, show business_name if NDA is signed, otherwise business_alias
                      if (deal.access_type === 'read') {
                        if (hasNda) {
                          return deal.business_name || 'Business Opportunity';
                        } else {
                          // Use business_alias (guaranteed to exist due to database constraints)
                          return deal.business_alias;
                        }
                      }
                      // For admin/write users, always show business_name
                      return deal.business_name || 'Business Opportunity';
                    })()}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2">
                    {deal.deal_value && (
                      <span>{formatCurrency(deal.deal_value, deal.currency)}</span>
                    )}
                    <span>•</span>
                    <span>Updated {new Date(deal.updated_at).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-4 text-center text-sm text-gray-500">
              No deals available. Create your first deal to get started.
            </div>
          )}

          {/* Manage Deals Link */}
          <div className="border-t border-gray-100">
            <Link
              to="/deals"
              className="block px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Manage All Deals
            </Link>
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}


